import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormProvider, FieldPath, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Mail, ShieldCheck, X } from "lucide-react";
import ApplicationHero from "@/components/landing/application/ApplicationHero";
import ContactInformationForm from "@/components/landing/application/ContactInformationForm";
import EducationalInformationForm from "@/components/landing/application/EducationalInformationForm";
import PaymentInformationForm from "@/components/landing/application/PaymentInformationForm";
import PersonalInformationForm from "@/components/landing/application/PersonalInformationForm";
import { EmailVerificationGate } from "@/components/landing/application/EmailVerificationGate";
import Footer from "@/components/layouts/Footer";
import Header from "@/components/layouts/Header";
import { Button } from "@/components/ui/button";
import {
  applicationSchema,
  APPLICATION_DEFAULT_VALUES,
  ApplicationFormValues,
} from "./applicationFormSchema";
import { admissionsService } from "@/services/admissionsService";
import {
  ApplicationPayload,
  ApplicationSupportingFiles,
  VerifyAdmissionApplication,
  VerifyAdmissionDocument,
} from "@/types/admissions";
import { NoticeVariant } from "@/types";
import { formRateLimiter } from "@/utils/formSecurity";
import { validateFileUpload } from "@/utils/security";
import { formatCurrencyAmount } from "@/utils/currency";
import { sanitizeQueryValue } from "@/utils/url";
import { paths } from "@/routes/path";
import { ApplicationActionDialog } from "@/components/landing/application/ApplicationActionDialog";
import { useActionDialog } from "@/hooks/useActionDialog";
import { useProgrammes } from "@/hooks/useProgrammes";

type DocumentKey = keyof ApplicationSupportingFiles;

const ALL_DOCUMENT_KEYS: DocumentKey[] = [
  "meansOfIdentification",
  "certificateFile",
  "resultFile",
  "paymentReceipt",
  "jambResultFile",
];

const REQUIRED_DOCUMENT_KEYS: DocumentKey[] = ["paymentReceipt"];

const DOCUMENT_KEY_ALIASES: Record<DocumentKey, string[]> = {
  meansOfIdentification: [
    "meansofidentification",
    "means-of-identification",
    "means_of_identification",
    "identification",
    "idcard",
    "id-card",
    "nationalid",
    "national-id",
    "passport",
  ],
  certificateFile: [
    "certificate",
    "certification",
    "cert",
    "diploma",
    "degree",
  ],
  resultFile: [
    "result",
    "olevel",
    "o-level",
    "olevelresult",
    "waec",
    "neco",
  ],
  paymentReceipt: [
    "receipt",
    "paymentreceipt",
    "payment-proof",
    "paymentproof",
    "teller",
    "invoice",
  ],
  jambResultFile: [
    "jamb",
    "jambresult",
    "jamb-score",
    "utme",
  ],
};

const DOCUMENT_ERROR_MESSAGES: Record<DocumentKey, string> = {
  meansOfIdentification: "Upload your means of identification document.",
  certificateFile: "Upload your certificate.",
  resultFile: "Upload your O'Level result.",
  paymentReceipt: "Upload your payment receipt.",
  jambResultFile: "Upload your JAMB result.",
};

const normalizeDocumentHint = (value: string): string =>
  value.toLowerCase().replace(/[\s_\-']/g, "");

const DOCUMENT_HINTS: Array<{ hint: string; key: DocumentKey }> =
  Object.entries(DOCUMENT_KEY_ALIASES).flatMap(([key, aliases]) => {
    const documentKey = key as DocumentKey;
    const normalizedAliases = [
      normalizeDocumentHint(documentKey),
      ...aliases.map(normalizeDocumentHint),
    ];
    return normalizedAliases.map((hint) => ({ hint, key: documentKey }));
  });

type SelectOption = {
  value: string;
  label: string;
};

type PostDialogNavigation = {
  path: string;
  replace?: boolean;
  state?: Record<string, unknown>;
};

const normalizeDateInput = (value?: string | null): string => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value.split("T")[0] ?? "";
  }

  return parsed.toISOString().split("T")[0];
};

const parseAmountValue = (value?: string | null): number | undefined => {
  if (!value) {
    return undefined;
  }

  const cleaned = value.replace(/[^\d.]/g, "");
  if (!cleaned) {
    return undefined;
  }

  const parsed = Number(cleaned);
  if (Number.isNaN(parsed) || parsed <= 0) {
    return undefined;
  }

  return parsed;
};

const detectDocumentType = (value: string): DocumentKey | null => {
  const raw = value?.toString?.() ?? "";
  if (!raw) {
    return null;
  }

  const normalized = raw.toLowerCase();
  const compact = normalizeDocumentHint(raw);

  for (const { hint, key } of DOCUMENT_HINTS) {
    if (normalized.includes(hint) || compact.includes(hint)) {
      return key;
    }
  }

  return null;
};

const getDocumentDisplayName = (url: string): string => {
  try {
    const segment = url.split("/").pop() ?? "";
    if (!segment) {
      return "Previously uploaded file";
    }
    return decodeURIComponent(segment);
  } catch {
    return "Previously uploaded file";
  }
};

const deriveExistingDocuments = (
  documents?: VerifyAdmissionApplication["documents"],
): Partial<Record<DocumentKey, string>> => {
  if (!Array.isArray(documents) || documents.length === 0) {
    return {};
  }

  return documents.reduce<Partial<Record<DocumentKey, string>>>((acc, entry) => {
    if (!entry) {
      return acc;
    }

    const collectStrings = (
      candidate: string | VerifyAdmissionDocument,
    ): string[] => {
      if (typeof candidate === "string") {
        return candidate.trim() ? [candidate] : [];
      }

      return Object.values(candidate).reduce<string[]>((list, value) => {
        if (typeof value === "string" && value.trim()) {
          list.push(value);
        }
        return list;
      }, []);
    };

    const stringCandidates = collectStrings(entry);

    if (stringCandidates.length === 0) {
      return acc;
    }

    const getTypeFromEntry = (
      candidate: string | VerifyAdmissionDocument,
    ): DocumentKey | null => {
      if (typeof candidate === "string") {
        return detectDocumentType(candidate);
      }

      const possibleTypeFields: Array<string | undefined> = [
        candidate.type,
        candidate.document_type,
        candidate.documentType,
      ];

      for (const field of possibleTypeFields) {
        if (typeof field === "string") {
          const detected = detectDocumentType(field);
          if (detected) {
            return detected;
          }
        }
      }

      return (
        stringCandidates
          .map((candidateValue) => detectDocumentType(candidateValue))
          .find((match): match is DocumentKey => Boolean(match)) ?? null
      );
    };

    const docType = getTypeFromEntry(entry);

    if (!docType || acc[docType]) {
      return acc;
    }

    const pickBestValue = (
      candidate: string | VerifyAdmissionDocument,
      fallbacks: string[],
    ): string | undefined => {
      if (typeof candidate === "string") {
        return candidate;
      }

      const prioritizedKeys: (keyof VerifyAdmissionDocument)[] = [
        "url",
        "link",
        "document",
        "value",
        "path",
        "storage_key",
        "storageKey",
      ];

      for (const key of prioritizedKeys) {
        const rawValue = candidate[key];
        if (typeof rawValue === "string" && rawValue.trim()) {
          return rawValue;
        }
      }

      return fallbacks.find((value) => typeof value === "string" && value);
    };

    const storedValue = pickBestValue(entry, stringCandidates);
    if (storedValue) {
      acc[docType] = storedValue;
    }

    return acc;
  }, {});
};

const mapApplicationToFormValues = (
  application: VerifyAdmissionApplication,
  programmeFallback?: string,
): ApplicationFormValues => {
  const educationSummary = Array.isArray(application.education_summary)
    ? application.education_summary[0] ?? {}
    : {};
  const payments = Array.isArray(application.payments)
    ? application.payments
    : [];
  const latestPayment = payments[0];

  const hasOLevel = educationSummary.hasOLevel ?? true;
  const jambScore =
    typeof application.jamb_score === "number" &&
      Number.isFinite(application.jamb_score)
      ? application.jamb_score
      : undefined;

  const resolvedCohort =
    application.programme_cohort_id ??
    application.cohort ??
    "";

  return {
    personal: {
      ...APPLICATION_DEFAULT_VALUES.personal,
      firstName: application.first_name ?? "",
      lastName: application.last_name ?? "",
      email: application.email ?? "",
      phone: application.phone ?? "",
      nationality: application.nationality ?? "",
      dob: normalizeDateInput(application.dob),
      gender: application.gender ?? "",
      employmentStatus: application.employment_status ?? "",
      programme:
        programmeFallback ??
        application.programme_id ??
        application.program_id ??
        application.programme ??
        "",
      cohort: resolvedCohort,
      address: application.address ?? "",
      country: application.country ?? "",
      state: application.state ?? "",
      city: application.city ?? "",
      lga: application.lga ?? "",
    },
    education: {
      ...APPLICATION_DEFAULT_VALUES.education,
      highestQualification: educationSummary.level ?? "",
      hasOLevel: hasOLevel ? "yes" : "no",
      oLevelTimes: educationSummary.oLevelTimes
        ? String(educationSummary.oLevelTimes)
        : APPLICATION_DEFAULT_VALUES.education.oLevelTimes,
      oLevelTypes: Array.isArray(educationSummary.oLevelTypes)
        ? educationSummary.oLevelTypes.filter(Boolean)
        : APPLICATION_DEFAULT_VALUES.education.oLevelTypes,
      wroteJamb: jambScore !== undefined ? "yes" : "no",
      jambScore: jambScore !== undefined ? String(jambScore) : "",
    },
    payment: {
      ...APPLICATION_DEFAULT_VALUES.payment,
      amount: latestPayment?.amount
        ? String(latestPayment.amount)
        : APPLICATION_DEFAULT_VALUES.payment.amount,
      reference: latestPayment?.reference ?? "",
    },
  };
};

const inferStepFromDraft = (values: ApplicationFormValues): number => {
  const personalComplete =
    Boolean(values.personal.firstName) &&
    Boolean(values.personal.lastName) &&
    Boolean(values.personal.phone) &&
    Boolean(values.personal.address);

  const educationComplete =
    Boolean(values.education.highestQualification) &&
    Array.isArray(values.education.oLevelTypes) &&
    values.education.oLevelTypes.filter(Boolean).length > 0;

  const paymentComplete = Boolean(values.payment.reference);

  if (!personalComplete) {
    return 1;
  }

  if (!educationComplete) {
    return 2;
  }

  if (!paymentComplete) {
    return 3;
  }

  return 3;
};

const noticeStyles: Record<
  NoticeVariant,
  { container: string; icon: React.ReactElement }
> = {
  success: {
    container: "border-[#CBEED8] bg-[#EAF6EE] text-[#166534]",
    icon: <ShieldCheck className="h-5 w-5 text-[#16A34A]" />,
  },
  info: {
    container: "border-[#DBEAFE] bg-[#EFF6FF] text-[#1D4ED8]",
    icon: <Mail className="h-5 w-5 text-[#1D4ED8]" />,
  },
  error: {
    container: "border-red-200 bg-red-50 text-red-600",
    icon: <Mail className="h-5 w-5 text-red-500" />,
  },
};

const STEP_FIELDS: Record<number, FieldPath<ApplicationFormValues>[]> = {
  1: [
    "personal.firstName",
    "personal.lastName",
    "personal.email",
    "personal.phone",
    "personal.nationality",
    "personal.dob",
    "personal.gender",
    "personal.employmentStatus",
    "personal.programme",
    "personal.cohort",
    "personal.address",
    "personal.country",
    "personal.state",
    "personal.lga",
    "personal.city",
  ],
  2: [
    "education.highestQualification",
    "education.hasOLevel",
    "education.oLevelTimes",
    "education.oLevelTypes",
    "education.wroteJamb",
    "education.jambScore",
  ],
  3: ["payment.reference"],
};

const fallbackBankDetails = {
  bankName: "Paystack-Titan",
  accountNumber: "0987744362",
  accountName: "Kaduna Business School",
  amount: "",
  currency: "NGN",
  instructions: "Use this account for all application payments.",
};

const ApplicationFormPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const programmeIdParam = useMemo(
    () => sanitizeQueryValue(searchParams.get("programmeId")),
    [searchParams],
  );
  const programmeNameParam =
    sanitizeQueryValue(searchParams.get("programmeName")) ||
    sanitizeQueryValue(searchParams.get("programmeTitle"));

  const [selectedProgrammeIdState, setSelectedProgrammeIdState] = useState(
    programmeIdParam || "",
  );
  const [selectedProgrammeLabelState, setSelectedProgrammeLabelState] = useState(
    programmeNameParam || "",
  );

  const { programmes: programmeList, isLoading: isProgrammesLoading } = useProgrammes();

  const programmeOptions = useMemo(
    () =>
      [...programmeList]
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((programme) => ({
          value: programme.id ?? '',
          label: programme.title,
        })),
    [programmeList],
  );

  const [currentStep, setCurrentStep] = useState(1);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [verificationNotice, setVerificationNotice] = useState<{
    message: string;
    variant: NoticeVariant;
  } | null>(null);
  const [sessionInfo, setSessionInfo] = useState<{
    sessionId: string | null;
    draftAppId: string | null;
    appId: string | null;
  }>({
    sessionId: null,
    draftAppId: null,
    appId: null,
  });
  const [files, setFiles] = useState<ApplicationSupportingFiles>({});
  const [fileErrors, setFileErrors] = useState<
    Partial<Record<keyof ApplicationSupportingFiles, string>>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [existingApplication, setExistingApplication] = useState<VerifyAdmissionApplication | null>(null);
  const [existingDocuments, setExistingDocuments] = useState<
    Partial<Record<DocumentKey, string>>
  >({});
  const [postDialogNavigation, setPostDialogNavigation] =
    useState<PostDialogNavigation | null>(null);
  const {
    dialog: blockingDialog,
    showDialog,
    hideDialog,
  } = useActionDialog();

  const methods = useForm<ApplicationFormValues>({
    resolver: zodResolver(applicationSchema),
    defaultValues: {
      ...APPLICATION_DEFAULT_VALUES,
      personal: {
        ...APPLICATION_DEFAULT_VALUES.personal,
        programme: selectedProgrammeIdState || "",
      },
    },
    mode: "onBlur",
  });

  const { handleSubmit, trigger, setValue, getValues, reset, watch } = methods;

  const resolveProgrammeSelection = useCallback(
    (rawValue?: string | null) => {
      const trimmedValue = rawValue?.trim() ?? "";
      let programmeId = trimmedValue;
      let programmeMatch = programmeList.find(
        (programme) => programme.id === programmeId,
      );

      if (!programmeMatch && trimmedValue) {
        const matchByTitle = programmeList.find(
          (programme) =>
            programme.title.toLowerCase() === trimmedValue.toLowerCase(),
        );
        if (matchByTitle) {
          programmeMatch = matchByTitle;
          programmeId = matchByTitle.id ?? "";
        }
      }

      if (!programmeMatch && !programmeId && programmeIdParam) {
        programmeId = programmeIdParam;
        programmeMatch = programmeList.find(
          (programme) => programme.id === programmeIdParam,
        );
      }

      const fallbackLabel =
        trimmedValue ||
        programmeNameParam ||
        selectedProgrammeLabelState ||
        programmeIdParam ||
        "";

      return {
        programmeId,
        programmeLabel: programmeMatch?.title ?? fallbackLabel,
        programmeMatch,
      };
    },
    [programmeIdParam, programmeList, programmeNameParam, selectedProgrammeLabelState],
  );

  useEffect(() => {
    if (!selectedProgrammeIdState) {
      return;
    }
    const match = programmeList.find(
      (programme) => programme.id === selectedProgrammeIdState,
    );
    if (match && selectedProgrammeLabelState !== match.title) {
      setSelectedProgrammeLabelState(match.title);
    }
  }, [programmeList, selectedProgrammeIdState, selectedProgrammeLabelState]);

  const handleProgrammeSelect = useCallback(
    (programme: { id?: string | null; title?: string | null }) => {
      const nextId = programme.id ?? "";
      const nextTitle = programme.title ?? "";

      setSelectedProgrammeIdState(nextId);
      setSelectedProgrammeLabelState(nextTitle || nextId);
      setValue("personal.programme", nextId, { shouldValidate: false });
    },
    [setValue],
  );

  useEffect(() => {
    if (verifiedEmail) {
      setValue("personal.email", verifiedEmail, { shouldValidate: false });
    }
  }, [verifiedEmail, setValue]);

  useEffect(() => {
    setValue("personal.programme", selectedProgrammeIdState || "", {
      shouldValidate: false,
    });
  }, [selectedProgrammeIdState, setValue]);

  const isEmailVerified = Boolean(verifiedEmail);

  const formProgrammeId = watch("personal.programme");

  const programmeResolution = useMemo(
    () => resolveProgrammeSelection(formProgrammeId),
    [resolveProgrammeSelection, formProgrammeId],
  );

  const formCohortId = watch("personal.cohort");

  const activeProgrammeId = programmeResolution.programmeId;
  const resolvedProgrammeLabel = programmeResolution.programmeLabel;
  const hasResolvedProgramme = Boolean(programmeResolution.programmeMatch);

  const availableProgrammeCohorts = useMemo(
    () => programmeResolution.programmeMatch?.cohorts ?? [],
    [programmeResolution.programmeMatch],
  );

  const selectedCohort = useMemo(
    () => availableProgrammeCohorts.find((c) => c.id === formCohortId),
    [availableProgrammeCohorts, formCohortId],
  );

  const cohortOptions = useMemo(
    () =>
      availableProgrammeCohorts
        .filter(
          (cohort) =>
            cohort.id.trim() !== "" && (cohort.isActive !== false),
        )
        .map<SelectOption>((cohort) => ({
          value: cohort.id,
          label: cohort.title.trim() ? cohort.title : cohort.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [availableProgrammeCohorts],
  );

  const resolveCohortSelection = useCallback(
    (rawValue?: string | null) => {
      const trimmedValue = rawValue?.trim() ?? "";

      if (!trimmedValue) {
        return { cohortId: "", cohortLabel: "" };
      }

      const matchById = cohortOptions.find(
        (option) => option.value === trimmedValue,
      );
      if (matchById) {
        return { cohortId: matchById.value, cohortLabel: matchById.label };
      }

      const matchByLabel = cohortOptions.find(
        (option) =>
          option.label.toLowerCase() === trimmedValue.toLowerCase(),
      );
      if (matchByLabel) {
        return { cohortId: matchByLabel.value, cohortLabel: matchByLabel.label };
      }

      const matchByCohort = availableProgrammeCohorts.find((cohort) => {
        const normalizedTitle = cohort.title.trim().toLowerCase();
        const normalizedId = cohort.id.trim().toLowerCase();
        const normalizedValue = trimmedValue.toLowerCase();
        return normalizedTitle === normalizedValue || normalizedId === normalizedValue;
      });

      if (matchByCohort) {
        return {
          cohortId: matchByCohort.id,
          cohortLabel: matchByCohort.title || matchByCohort.id,
        };
      }

      return { cohortId: trimmedValue, cohortLabel: trimmedValue };
    },
    [availableProgrammeCohorts, cohortOptions],
  );

  useEffect(() => {
    const currentValue = getValues("personal.cohort");

    if (!activeProgrammeId) {
      if (currentValue) {
        setValue("personal.cohort", "", { shouldValidate: false });
      }
      return;
    }

    if (cohortOptions.length === 0) {
      if (currentValue && hasResolvedProgramme) {
        setValue("personal.cohort", "", { shouldValidate: true });
      }
      return;
    }

    if (!currentValue) {
      if (cohortOptions.length === 1) {
        setValue("personal.cohort", cohortOptions[0].value, {
          shouldValidate: true,
        });
      }
      return;
    }

    const exactMatch = cohortOptions.some(
      (option) => option.value === currentValue,
    );

    if (exactMatch) {
      return;
    }

    const matchByLabel = cohortOptions.find(
      (option) => option.label.toLowerCase() === currentValue.toLowerCase(),
    );

    if (matchByLabel) {
      setValue("personal.cohort", matchByLabel.value, {
        shouldValidate: false,
      });
    } else {
      setValue("personal.cohort", "", { shouldValidate: true });
    }
  }, [activeProgrammeId, cohortOptions, getValues, hasResolvedProgramme, setValue]);

  const primaryPaymentAccount = useMemo(() => {
    const cohort =
      selectedCohort ||
      availableProgrammeCohorts.find((c) => c.isActive) ||
      availableProgrammeCohorts[0];
    if (!cohort) return undefined;

    const accounts = cohort.payment_accounts;
    const activeAccounts = accounts.filter((account) => account.is_active);
    return (
      activeAccounts.find((account) => account.is_default) ??
      activeAccounts[0] ??
      accounts.find((account) => account.is_default) ??
      accounts[0]
    );
  }, [selectedCohort, availableProgrammeCohorts]);

  const bankDetails = useMemo(() => {
    const normalizeString = (value?: string | null) =>
      typeof value === "string" && value.trim() ? value.trim() : undefined;

    const feeFromCohort =
      selectedCohort?.fee !== undefined ? String(selectedCohort.fee) : undefined;

    const amountFromAccount =
      primaryPaymentAccount?.amount !== undefined
        ? String(primaryPaymentAccount.amount)
        : undefined;

    const resolvedAmount =
      feeFromCohort ?? amountFromAccount ?? fallbackBankDetails.amount;

    const resolvedCurrency =
      normalizeString(selectedCohort?.currency) ??
      normalizeString(primaryPaymentAccount?.currency) ??
      fallbackBankDetails.currency;

    return {
      bankName:
        normalizeString(primaryPaymentAccount?.bankName) ??
        fallbackBankDetails.bankName,
      accountNumber:
        normalizeString(primaryPaymentAccount?.accountNumber) ??
        fallbackBankDetails.accountNumber,
      accountName:
        normalizeString(primaryPaymentAccount?.accountName) ??
        fallbackBankDetails.accountName,
      amount: resolvedAmount,
      currency: resolvedCurrency.toUpperCase(),
      instructions:
        normalizeString(primaryPaymentAccount?.instructions) ??
        fallbackBankDetails.instructions,
    };
  }, [primaryPaymentAccount, selectedCohort]);

  const programmeDisplayPrice = useMemo(() => {
    if (selectedCohort?.fee !== undefined) {
      return formatCurrencyAmount(selectedCohort.fee, selectedCohort.currency);
    }
    const label = programmeResolution.programmeMatch?.priceLabel;
    return (
      label ?? formatCurrencyAmount(bankDetails.amount, bankDetails.currency)
    );
  }, [
    selectedCohort,
    bankDetails.amount,
    bankDetails.currency,
    programmeResolution.programmeMatch,
  ]);

  const pageName = useMemo(() => {
    switch (currentStep) {
      case 1:
        return "Personal Information";
      case 2:
        return "Educational Information";
      case 3:
        return "Payment Information";
      default:
        return "Personal Information";
    }
  }, [currentStep]);

  const pageLabel = useMemo(
    () =>
      currentStep === 3
        ? "Choose payment method"
        : "Fill all required details",
    [currentStep],
  );

  const dismissNotice = () => setVerificationNotice(null);

  const activeNotice = verificationNotice
    ? noticeStyles[verificationNotice.variant]
    : null;

  const handleFileUpload = useCallback(
    (key: keyof ApplicationSupportingFiles) => (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) {
        setFiles((prev) => ({ ...prev, [key]: undefined }));
        setFileErrors((prev) => ({ ...prev, [key]: undefined }));
        return;
      }

      const file = fileList[0];
      const validation = validateFileUpload(file);

      if (!validation.isValid) {
        setFileErrors((prev) => ({
          ...prev,
          [key]: validation.error ?? "Invalid file selected",
        }));
        setFiles((prev) => ({ ...prev, [key]: undefined }));
        return;
      }

      setFiles((prev) => ({ ...prev, [key]: file }));
      setFileErrors((prev) => ({ ...prev, [key]: undefined }));
      setExistingDocuments((prev) => {
        if (!prev[key as DocumentKey]) {
          return prev;
        }
        const next = { ...prev };
        delete next[key as DocumentKey];
        return next;
      });
    },
    [],
  );

  const handleVerificationSuccess = useCallback(
    ({
      email,
      message,
      variant,
      sessionId,
      draftAppId,
      appId,
      application,
    }: {
      email: string;
      message: string;
      variant?: NoticeVariant;
      sessionId?: string;
      draftAppId?: string;
      appId?: string;
      application?: VerifyAdmissionApplication | null;
      programmeCohortId?: string | null;
      programme_cohort_id?: string | null;
    }) => {
      const resolvedEmail = application?.email ?? email;
      const resolvedDraftId = draftAppId ?? application?.id ?? null;
      const resolvedAppId = appId ?? application?.app_id ?? null;
      const resolvedProgrammeCohortId = application?.programme_cohort_id ?? null;
      const programmeFallback =
        programmeNameParam ||
        selectedProgrammeLabelState ||
        programmeIdParam ||
        undefined;

      setVerifiedEmail(resolvedEmail);
      setSessionInfo((prev) => ({
        sessionId: sessionId ?? prev.sessionId ?? null,
        draftAppId: resolvedDraftId ?? prev.draftAppId ?? null,
        appId: resolvedAppId ?? prev.appId ?? null,
      }));

      if (application) {
        const mappedValues = mapApplicationToFormValues(
          application,
          programmeFallback,
        );
        const documentMap = deriveExistingDocuments(application.documents);
        reset(mappedValues, { keepDefaultValues: true });
        const resolvedProgramme = resolveProgrammeSelection(
          mappedValues.personal.programme ||
          selectedProgrammeIdState ||
          programmeIdParam,
        );
        setSelectedProgrammeIdState(resolvedProgramme.programmeId);
        setSelectedProgrammeLabelState(resolvedProgramme.programmeLabel);
        setValue("personal.programme", resolvedProgramme.programmeId, {
          shouldValidate: false,
        });
        setExistingApplication(application);
        setExistingDocuments(documentMap);
        setCurrentStep(inferStepFromDraft(mappedValues));
      } else {
        if (resolvedProgrammeCohortId) {
          setValue("personal.cohort", resolvedProgrammeCohortId, {
            shouldValidate: true,
          });
        }
        setExistingApplication(null);
        setExistingDocuments({});
        setCurrentStep(1);
        const resolvedProgramme = resolveProgrammeSelection(
          selectedProgrammeIdState || programmeIdParam,
        );
        setSelectedProgrammeIdState(resolvedProgramme.programmeId);
        setSelectedProgrammeLabelState(resolvedProgramme.programmeLabel);
      }
      setFiles({});
      setFileErrors({});

      setVerificationNotice({
        message,
        variant: variant ?? "success",
      });
      setSubmissionError(null);
    },
    [
      programmeIdParam,
      programmeNameParam,
      reset,
      resolveProgrammeSelection,
      selectedProgrammeIdState,
      selectedProgrammeLabelState,
      setValue,
    ],
  );

  const handleRequestOtp = useCallback(
    async (email: string) => {
      const activeProgrammeId = selectedProgrammeIdState || programmeIdParam;

      if (!activeProgrammeId) {
        throw new Error(
          "Select a programme to continue your application.",
        );
      }

      const limiterKey = `application-otp-${email.toLowerCase()}`;
      if (!formRateLimiter.canSubmit(limiterKey, "general")) {
        throw new Error(
          "Too many verification attempts. Please try again later.",
        );
      }

      const response = await admissionsService.startAdmission(email);
      setSessionInfo({
        sessionId: response.data.sessionId ?? null,
        draftAppId: null,
        appId: null,
      });

      return {
        message:
          response.message ||
          `A verification code has been sent to ${email}.`,
        sessionId: response.data.sessionId,
        expiresAt: response.data.expiresAt,
      };
    },
    [programmeIdParam, selectedProgrammeIdState],
  );

  const handleConfirmOtp = useCallback(
    async ({
      email,
      code,
      sessionId,
    }: {
      email: string;
      code: string;
      sessionId?: string;
    }) => {
      const activeSessionId = sessionId ?? sessionInfo.sessionId;
      const activeProgrammeId = selectedProgrammeIdState || programmeIdParam;

      if (!activeProgrammeId) {
        throw new Error(
          "Select a programme to continue your application.",
        );
      }

      if (!activeSessionId) {
        throw new Error(
          "Your verification session has expired. Please request a new code.",
        );
      }

      const response = await admissionsService.verifyAdmission(
        activeSessionId,
        code,
        activeProgrammeId,
      );

      const verifiedApplication = response.data.application ?? null;

      setSessionInfo({
        sessionId: response.data.sessionId ?? activeSessionId,
        draftAppId: response.data.draftAppId ?? verifiedApplication?.id ?? null,
        appId: response.data.appId ?? verifiedApplication?.app_id ?? null,
      });
      setVerifiedEmail(verifiedApplication?.email ?? email);

      return {
        success: response.success,
        message:
          response.message ||
          "Email verified successfully! You can continue your application.",
        variant: "success" as NoticeVariant,
        sessionId: response.data.sessionId,
        draftAppId: response.data.draftAppId,
        appId: response.data.appId,
        application: verifiedApplication,
      };
    },
    [programmeIdParam, selectedProgrammeIdState, sessionInfo.sessionId],
  );

  const composeApplicationPayload = useCallback(
    (formData: ApplicationFormValues, submit: boolean) => {
      const { programmeId: resolvedProgrammeId, programmeLabel } =
        resolveProgrammeSelection(formData.personal.programme);

      if (!resolvedProgrammeId) {
        throw new Error(
          "Programme information is missing. Please select a programme to continue.",
        );
      }

      const { cohortId: resolvedCohortId, cohortLabel } = resolveCohortSelection(
        formData.personal.cohort,
      );

      if (!resolvedCohortId) {
        throw new Error("Select a cohort for the chosen programme to continue.");
      }

      const isKnownCohort = availableProgrammeCohorts.some(
        (cohort) => cohort.id === resolvedCohortId,
      );

      if (!isKnownCohort) {
        throw new Error(
          "Select a valid cohort for the chosen programme before continuing.",
        );
      }

      const oLevelTimesValue = Number.parseInt(
        formData.education.oLevelTimes || "0",
        10,
      );
      const normalizedOLevelTimes = Number.isFinite(oLevelTimesValue) && oLevelTimesValue > 0
        ? oLevelTimesValue
        : 0;

      let jambScoreValue: number | undefined;
      if (formData.education.wroteJamb === "yes") {
        const parsedJamb = formData.education.jambScore
          ? Number(formData.education.jambScore)
          : undefined;
        if (
          submit &&
          (parsedJamb === undefined ||
            Number.isNaN(parsedJamb) ||
            parsedJamb <= 0)
        ) {
          throw new Error("Please provide a valid JAMB score.");
        }

        if (parsedJamb !== undefined && !Number.isNaN(parsedJamb) && parsedJamb > 0) {
          jambScoreValue = parsedJamb;
        }
      }

      const normalizedAmount =
        parseAmountValue(formData.payment.amount) ??
        parseAmountValue(bankDetails.amount);

      const paymentReference = formData.payment.reference?.trim() ?? "";

      const paymentProvided =
        Boolean(paymentReference) && normalizedAmount !== undefined;

      const oLevelTypes =
        Array.isArray(formData.education.oLevelTypes) &&
          formData.education.oLevelTypes.length > 0
          ? formData.education.oLevelTypes.filter(Boolean)
          : [];

      const payload = {
        personal: {
          ...formData.personal,
          programme: programmeLabel || resolvedProgrammeId,
          cohort: cohortLabel || resolvedCohortId,
        },
        educationSummary: [
          {
            level: formData.education.highestQualification,
            hasOLevel: formData.education.hasOLevel === "yes",
            oLevelTimes: normalizedOLevelTimes,
            oLevelTypes,
          },
        ],
        programId: resolvedProgrammeId,
        programmeCohortId: resolvedCohortId,
        submit,
      } as ApplicationPayload;

      if (jambScoreValue !== undefined) {
        payload.jambScore = jambScoreValue;
      }

      if (paymentProvided && normalizedAmount !== undefined) {
        payload.payment = {
          amount: normalizedAmount,
          provider: "receipt_upload",
          reference: paymentReference,
        };
      }

      return { payload, paymentProvided };
    },
    [
      availableProgrammeCohorts,
      bankDetails.amount,
      resolveCohortSelection,
      resolveProgrammeSelection,
    ],
  );

  const persistApplication = useCallback(
    async (formData: ApplicationFormValues, submit: boolean) => {
      if (!sessionInfo.sessionId) {
        throw new Error(
          "Please verify your email before saving the application.",
        );
      }

      const { payload, paymentProvided } = composeApplicationPayload(
        formData,
        submit,
      );

      if (submit && !paymentProvided) {
        throw new Error("Payment information is required to submit.");
      }

      const result = await admissionsService.createApplication({
        sessionId: sessionInfo.sessionId,
        applicationId:
          sessionInfo.draftAppId ?? existingApplication?.id ?? undefined,
        payload,
        files: {
          meansOfIdentification: files.meansOfIdentification,
          certificateFile: files.certificateFile,
          resultFile: files.resultFile,
          paymentReceipt: files.paymentReceipt,
          jambResultFile: files.jambResultFile,
        },
      });

      setSessionInfo((prev) => ({
        sessionId: prev.sessionId ?? sessionInfo.sessionId,
        draftAppId: result.id ?? prev.draftAppId ?? null,
        appId: result.appId ?? prev.appId ?? null,
      }));

      return { result, paymentProvided };
    },
    [
      composeApplicationPayload,
      existingApplication?.id,
      files.certificateFile,
      files.jambResultFile,
      files.meansOfIdentification,
      files.paymentReceipt,
      files.resultFile,
      sessionInfo.draftAppId,
      sessionInfo.sessionId,
    ],
  );

  const ensureRequiredDocuments = useCallback(
    (formData: ApplicationFormValues) => {
      const requiredKeys: DocumentKey[] = [...REQUIRED_DOCUMENT_KEYS];

      const missing = requiredKeys.filter(
        (key) => !files[key] && !existingDocuments[key],
      );

      setFileErrors((prev) => {
        const next: Partial<Record<keyof ApplicationSupportingFiles, string>> = {
          ...prev,
        };
        ALL_DOCUMENT_KEYS.forEach((key) => {
          if (!requiredKeys.includes(key)) {
            next[key] = undefined;
          }
        });
        requiredKeys.forEach((key) => {
          next[key] = missing.includes(key)
            ? DOCUMENT_ERROR_MESSAGES[key]
            : undefined;
        });
        return next;
      });

      if (missing.length > 0) {
        const needsEducationalDocs = missing.some(
          (key) =>
            key === "certificateFile" ||
            key === "resultFile" ||
            key === "jambResultFile",
        );
        const needsPaymentReceipt = missing.includes("paymentReceipt");
        const targetStep = needsPaymentReceipt ? 3 : needsEducationalDocs ? 2 : 1;
        setCurrentStep(targetStep);
        const missingMessages = missing.map((key) => DOCUMENT_ERROR_MESSAGES[key]);
        setSubmissionError(
          "Please upload all required documents before submitting.",
        );
        showDialog({
          title: "Upload required documents",
          description: "Before submitting your application",
          items: missingMessages,
          acknowledgeLabel: "I'll upload them",
        });
        return false;
      }

      hideDialog();

      return true;
    },
    [existingDocuments, files, hideDialog, showDialog],
  );

  const handleAdvanceStep = useCallback(async () => {
    const fields = STEP_FIELDS[currentStep];
    setIsAdvancing(true);
    setSubmissionError(null);
    try {
      const isValid = fields
        ? await trigger(fields, { shouldFocus: true })
        : true;

      if (!isValid) {
        return;
      }

      const formValues = getValues();
      await persistApplication(formValues, false);

      setCurrentStep((prev) => Math.min(prev + 1, 3));
    } catch (error: any) {
      setSubmissionError(
        error?.message ||
        "We could not save your application. Please try again.",
      );
    } finally {
      setIsAdvancing(false);
    }
  }, [currentStep, getValues, persistApplication, trigger]);

  const handleDialogClose = useCallback(() => {
    hideDialog();
    setPostDialogNavigation((nextNavigation) => {
      if (nextNavigation) {
        navigate(nextNavigation.path, {
          replace: nextNavigation.replace,
          state: nextNavigation.state,
        });
      }
      return null;
    });
  }, [hideDialog, navigate]);

  const handleApplicationSubmit = useCallback(
    async (formData: ApplicationFormValues) => {
      const resolvedProgrammeId =
        resolveProgrammeSelection(formData.personal.programme).programmeId ||
        selectedProgrammeIdState ||
        programmeIdParam;

      if (!resolvedProgrammeId) {
        setSubmissionError(
          "Select a programme to continue your application.",
        );
        setCurrentStep(1);
        return;
      }

      if (!sessionInfo.sessionId) {
        setSubmissionError(
          "Please verify your email before submitting the application.",
        );
        setCurrentStep(1);
        return;
      }

      const submitLimiterKey = `application-submit-${(
        verifiedEmail ?? formData.personal.email
      ).toLowerCase()}`;
      if (!formRateLimiter.canSubmit(submitLimiterKey, "general")) {
        setSubmissionError(
          "Too many submission attempts. Please wait a moment and try again.",
        );
        return;
      }

      if (!ensureRequiredDocuments(formData)) {
        return;
      }

      setIsSubmitting(true);
      setSubmissionError(null);

      try {
        const { result } = await persistApplication(formData, true);

        setVerificationNotice({
          message: `Application submitted successfully. Your application ID is ${result.appId}.`,
          variant: "success",
        });

        setPostDialogNavigation({
          path: paths.public.home.absolute,
          replace: true,
          state: {
            fromApplication: true,
            appId: result.appId,
            email: verifiedEmail ?? formData.personal.email,
          },
        });

        showDialog({
          title: "Application submitted successfully",
          description:
            "Your application has been received and is awaiting review.",
          acknowledgeLabel: "Go home",
        });
      } catch (error: any) {
        setSubmissionError(
          error?.message ||
          "We could not submit your application. Please try again.",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      ensureRequiredDocuments,
      persistApplication,
      programmeIdParam,
      resolveProgrammeSelection,
      sessionInfo.sessionId,
      showDialog,
      selectedProgrammeIdState,
      verifiedEmail,
    ],
  );

  const handleFinalSubmitClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const currentValues = getValues();
      if (!ensureRequiredDocuments(currentValues)) {
        return;
      }

      await handleSubmit(handleApplicationSubmit)();
    },
    [ensureRequiredDocuments, getValues, handleApplicationSubmit, handleSubmit],
  );

  return (
    <>
      {/* <Header /> */}
      <main className="min-h-screen bg-gray-50">
        <ApplicationHero />
        <section className="container mx-auto px-4 py-16 md:px-8 lg:px-16">
          {!isEmailVerified ? (
            <div className="mx-auto max-w-3xl">
              <EmailVerificationGate
                onVerified={handleVerificationSuccess}
                requestOtp={handleRequestOtp}
                confirmOtp={handleConfirmOtp}
                programmes={programmeList}
                isProgrammeLoading={isProgrammesLoading}
                selectedProgrammeId={selectedProgrammeIdState}
                selectedProgrammeName={selectedProgrammeLabelState}
                onProgrammeSelect={handleProgrammeSelect}
              />
            </div>
          ) : (
            <div className="rounded-2xl bg-white p-8 shadow-md md:p-12">
              {activeNotice && (
                <div
                  className={`mb-8 flex items-start justify-between gap-4 rounded-xl border px-4 py-3 text-sm ${activeNotice.container}`}
                >
                  <div className="flex items-center gap-2">
                    {activeNotice.icon}
                    <span>{verificationNotice?.message}</span>
                  </div>
                  <button
                    type="button"
                    onClick={dismissNotice}
                    className="rounded-full p-1 text-current transition hover:bg-black/5"
                    aria-label="Dismiss verification notice"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              <div className="mb-10 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl text-gray-900">{pageName}</h2>
                  <p className="mt-1 text-gray-500">{pageLabel}</p>
                </div>
                <span className="text-sm">
                  <span className="font-bold">{currentStep}</span> /{" "}
                  <span className="font-bold text-gray-500">3</span>
                </span>
              </div>

              <FormProvider {...methods}>
                <form
                  onSubmit={handleSubmit(handleApplicationSubmit)}
                  className="space-y-12"
                  noValidate
                >
                  {currentStep === 1 && (
                    <>
                      <PersonalInformationForm
                        verifiedEmail={verifiedEmail ?? undefined}
                        isEmailLocked
                        programmeOptions={programmeOptions}
                        isProgrammeLoading={isProgrammesLoading}
                        cohortOptions={cohortOptions}
                        isCohortLoading={isProgrammesLoading}
                      />

                      <div>
                        <div className="my-12">
                          <h2 className="text-2xl text-gray-900">
                            Contact Information
                          </h2>
                          <p className="mt-1 text-gray-500">
                            Provide your up-to-date contact details
                          </p>
                        </div>
                        <ContactInformationForm
                          onIdentificationSelect={handleFileUpload(
                            "meansOfIdentification",
                          )}
                          identificationFileName={
                            files.meansOfIdentification?.name
                          }
                          identificationError={
                            fileErrors.meansOfIdentification
                          }
                          existingIdentificationUrl={
                            existingDocuments.meansOfIdentification
                          }
                          existingIdentificationName={
                            existingDocuments.meansOfIdentification
                              ? getDocumentDisplayName(
                                existingDocuments.meansOfIdentification,
                              )
                              : undefined
                          }
                        />
                      </div>
                    </>
                  )}

                  {currentStep === 2 && (
                    <EducationalInformationForm
                      onCertificateSelect={handleFileUpload("certificateFile")}
                      certificateFileName={files.certificateFile?.name}
                      certificateError={fileErrors.certificateFile}
                      existingCertificateUrl={existingDocuments.certificateFile}
                      existingCertificateName={
                        existingDocuments.certificateFile
                          ? getDocumentDisplayName(
                            existingDocuments.certificateFile,
                          )
                          : undefined
                      }
                      onResultSelect={handleFileUpload("resultFile")}
                      resultFileName={files.resultFile?.name}
                      resultError={fileErrors.resultFile}
                      existingResultUrl={existingDocuments.resultFile}
                      existingResultName={
                        existingDocuments.resultFile
                          ? getDocumentDisplayName(existingDocuments.resultFile)
                          : undefined
                      }
                      onJambResultSelect={handleFileUpload("jambResultFile")}
                      jambResultFileName={files.jambResultFile?.name}
                      jambResultError={fileErrors.jambResultFile}
                      existingJambResultUrl={existingDocuments.jambResultFile}
                      existingJambResultName={
                        existingDocuments.jambResultFile
                          ? getDocumentDisplayName(
                            existingDocuments.jambResultFile,
                          )
                          : undefined
                      }
                    />
                  )}

                  {currentStep === 3 && (
                    <PaymentInformationForm
                      bankDetails={bankDetails}
                      programmeName={resolvedProgrammeLabel}
                      programmePrice={programmeDisplayPrice}
                      onReceiptSelect={handleFileUpload("paymentReceipt")}
                      receiptFileName={files.paymentReceipt?.name}
                      receiptError={fileErrors.paymentReceipt}
                      existingReceiptUrl={existingDocuments.paymentReceipt}
                      existingReceiptName={
                        existingDocuments.paymentReceipt
                          ? getDocumentDisplayName(
                            existingDocuments.paymentReceipt,
                          )
                          : undefined
                      }
                    />
                  )}

                  {submissionError && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                      {submissionError}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3">
                    {currentStep > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-md border bg-gray-100 px-6 py-2 text-gray-700 hover:bg-gray-200"
                        onClick={() =>
                          setCurrentStep((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={isAdvancing || isSubmitting}
                      >
                        Previous
                      </Button>
                    )}

                    {currentStep < 3 ? (
                      <Button
                        type="button"
                        onClick={handleAdvanceStep}
                        disabled={isAdvancing || isSubmitting}
                        className="rounded-md px-6 py-2"
                      >
                        {isAdvancing ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Checking...
                          </span>
                        ) : (
                          "Continue"
                        )}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={handleFinalSubmitClick}
                        disabled={isSubmitting}
                        className="rounded-md px-6 py-2"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Submitting...
                          </span>
                        ) : (
                          "Submit Application"
                        )}
                      </Button>
                    )}
                  </div>
                </form>
              </FormProvider>
            </div>
          )}
        </section>
      </main>
      <Footer />
      <ApplicationActionDialog
        dialog={blockingDialog}
        onClose={handleDialogClose}
      />
    </>
  );
};

export default ApplicationFormPage;
